import { type ImgHTMLAttributes, type ReactNode } from 'react'
import { useImageRetry } from '@/hooks/ui/useImageRetry'

interface RetryingImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError' | 'onLoad'> {
  src: string | null | undefined
  alt: string
  /** Rendered when there is no `src`, or after every retry is exhausted. */
  fallback?: ReactNode
}

/**
 * `<img>` for external CDN card/product images (TCGPlayer / OPTCG) that self-heals
 * via {@link useImageRetry}: a failed load is retried with a cache-bust param
 * instead of latching to the fallback. Being a component (not a bare hook) it can
 * be dropped into inline JSX, `.map()`s, and grid-config render functions where a
 * hook can't be called directly.
 *
 * Do NOT use this for the export grid image — that needs `crossOrigin` for
 * html-to-image canvas capture, and a cache-busted retry would re-trigger CORS and
 * risk tainting the canvas.
 */
export function RetryingImage({ src, alt, fallback = null, ...imgProps }: RetryingImageProps) {
  const { src: resolvedSrc, onError, onLoad, failed } = useImageRetry(src)

  if (!resolvedSrc || failed) return <>{fallback}</>

  // Controlled attrs come after the spread so callers can never clobber them
  // (the prop type also Omits them, but this keeps the invariant self-enforcing).
  return <img {...imgProps} src={resolvedSrc} alt={alt} onError={onError} onLoad={onLoad} />
}
